import { useEffect } from "react"
import { signal } from "@preact/signals-react"

import { addNotification } from "../stores/notification-store"
import { NOT_FOUND } from "../utils/constants"

export const profileSchema = signal({})
export const error = signal(null)
export const loading = signal(false)
export const isLoaded = signal(false)
const version = signal(null)
export const modified = signal(false) // allows a component to flag the data as modified so subsequent calls to the hook will reload it
export const parameterHash = signal(null)

const fetchData = async (businessEntityId, utgVersion, manifest, config, accessToken) => {

    const hash = await import('object-hash').then(module => module.default || module)
    // if any parameters have changed then flag as modified so that the hook will reload the data
    const currentParametersHash = hash([businessEntityId, utgVersion, manifest, config, accessToken])
    if (currentParametersHash != parameterHash.value) {
        modified.value = true
        parameterHash.value = currentParametersHash
    }

    // short-circuit if we have a pending request, are waiting for parameters or have already received an error
    if (loading.value || !businessEntityId || !utgVersion || !manifest || !config || !accessToken || error.value || !modified.value) {
        return
    }

    // short-circuit if we already have the data
    if (version.value == utgVersion && !modified.value) {
        console.log("Version is already the UTG version \"".concat(version.value).concat("\""))
        return
    }
    
    try {
        loading.value = true

        let manifestProfileEntry
        if (utgVersion == NOT_FOUND) {
            // load the default profile and add an info alert
            manifestProfileEntry = manifest.profiles.find(p => p.default)
            if (!manifestProfileEntry) {
                throw new Error(`Unable to locate either a default or specific profile schema manifest entry for ${utgVersion}.`)
            }
            version.value = manifestProfileEntry.utgVersion
            // add an info alert to warn the user that we loaded a default profile schema rather than a specific one for the utgVersion provided
            addNotification(`A default schema designed for UTG ${manifestProfileEntry.utgVersion} is loading. Any changes to this profile will associate it with this schema version. If not appropriate for this profile, please edit it in the legacy environment.`, 'info', false, 'Notice')
        }
        else {
            version.value = utgVersion; // allows us to short-circuit future calls when the data is already loaded       

            // load the appropriate profile schema from the file linked in the schema manifest
            manifestProfileEntry = manifest.profiles.find(p => p.utgVersion == utgVersion)

            if (!manifestProfileEntry) {
                // no profile schema for this utg. load the default profile and add an info alert
                // find the default profile
                manifestProfileEntry = manifest.profiles.find(p => p.default)
                if (!manifestProfileEntry) {
                    throw new Error(`Unable to locate either a default or specific profile schema manifest entry for ${utgVersion}.`)
                }
                // add an info alert to warn the user that we loaded a default profile schema rather than a specific one for the utgVersion provided
                addNotification(`A default schema designed for UTG ${utgVersion} is loading and may not be appropriate for this profile.`, 'info', false, 'Warning')
            }
        }

        // TODO: the manifest file assumes all schemas are in the /react/* folder. 
        // we'll have to adjust the manifest file but for now remove this path

        const filePath = manifestProfileEntry.file.startsWith('/react') ? manifestProfileEntry.file.slice(6) :  manifestProfileEntry.file
        const response = await fetch(filePath)
        let data = await response.json()
        profileSchema.value = data
        isLoaded.value = true
        return data

    }
    catch(err) {
        error.value = err
        console.log(err)
        
        addNotification(`Unable to load data due to the following error - ${err}`)
    }
    finally {
        loading.value = false
        modified.value = false
    }  
    
}

export default function useProfileSchema(businessEntityId, utgVersion, manifest, config, accessToken) {
    useEffect(() => {
        fetchData(businessEntityId, utgVersion, manifest, config, accessToken)    
    }, [businessEntityId, utgVersion, manifest, config, accessToken])

    return {
        profileSchema, loading, isLoaded, error, version, fetchData
    }
}